import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

const STATUS_COLORS = {
  overdue: { bg: "#FEF2F2", text: "#DC2626", dot: "#EF4444", label: "Overdue" },
  due_soon: { bg: "#FFFBEB", text: "#D97706", dot: "#F59E0B", label: "Due Soon" },
  upcoming: { bg: "#F0FDF4", text: "#16A34A", dot: "#22C55E", label: "Upcoming" },
};

function classifyInvoice(dueDateStr) {
  if (!dueDateStr) return "upcoming";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "overdue";
  if (diffDays <= 7) return "due_soon";
  return "upcoming";
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getDaysLabel(dueDateStr) {
  if (!dueDateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return "Due today";
  if (diffDays === 1) return "Due tomorrow";
  return `Due in ${diffDays}d`;
}

export default function OutstandingInvoicesWidget() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | overdue | due_soon | upcoming
  const [sortBy, setSortBy] = useState("due_date"); // due_date | amount | client

  useEffect(() => {
    fetchInvoices();
  }, []);

  async function fetchInvoices() {
    setLoading(true);
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id,
        invoice_number,
        description,
        total_amount,
        due_date,
        status,
        clients (
          full_name,
          business_name
        )
      `)
      .in("status", ["sent", "overdue"])
      .order("due_date", { ascending: true });

    if (!error && data) {
      setInvoices(data);
    }
    setLoading(false);
  }

  const classified = invoices.map((inv) => ({
    ...inv,
    urgency: classifyInvoice(inv.due_date),
  }));

  const filtered =
    filter === "all"
      ? classified
      : classified.filter((inv) => inv.urgency === filter);

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "due_date") {
      return new Date(a.due_date || "9999") - new Date(b.due_date || "9999");
    }
    if (sortBy === "amount") return b.total_amount - a.total_amount;
    if (sortBy === "client") {
      const nameA = a.clients?.full_name || "";
      const nameB = b.clients?.full_name || "";
      return nameA.localeCompare(nameB);
    }
    return 0;
  });

  const totalOutstanding = filtered.reduce(
    (sum, inv) => sum + Number(inv.total_amount || 0),
    0
  );
  const overdueTotal = classified
    .filter((i) => i.urgency === "overdue")
    .reduce((sum, i) => sum + Number(i.total_amount || 0), 0);
  const overdueCount = classified.filter((i) => i.urgency === "overdue").length;
  const dueSoonCount = classified.filter((i) => i.urgency === "due_soon").length;

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Outstanding Invoices</h2>
          <p style={styles.subtitle}>
            {classified.length} invoice{classified.length !== 1 ? "s" : ""} ·{" "}
            <span style={styles.totalHighlight}>
              {formatCurrency(
                classified.reduce((s, i) => s + Number(i.total_amount || 0), 0)
              )}{" "}
              total due
            </span>
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchInvoices} title="Refresh">
          ↻
        </button>
      </div>

      {/* Summary Chips */}
      {overdueCount > 0 && (
        <div style={styles.alertBanner}>
          <span style={styles.alertDot} />
          <span>
            <strong>{overdueCount} overdue</strong> · {formatCurrency(overdueTotal)} past due
          </span>
        </div>
      )}

      {/* Filter + Sort bar */}
      <div style={styles.toolbar}>
        <div style={styles.filters}>
          {["all", "overdue", "due_soon", "upcoming"].map((f) => {
            const labels = {
              all: "All",
              overdue: "Overdue",
              due_soon: "Due Soon",
              upcoming: "Upcoming",
            };
            const counts = {
              all: classified.length,
              overdue: classified.filter((i) => i.urgency === "overdue").length,
              due_soon: classified.filter((i) => i.urgency === "due_soon").length,
              upcoming: classified.filter((i) => i.urgency === "upcoming").length,
            };
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  ...styles.filterBtn,
                  ...(filter === f ? styles.filterBtnActive : {}),
                }}
              >
                {labels[f]}{" "}
                <span style={styles.filterCount}>{counts[f]}</span>
              </button>
            );
          })}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={styles.sortSelect}
        >
          <option value="due_date">Sort: Due Date</option>
          <option value="amount">Sort: Amount</option>
          <option value="client">Sort: Client</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={styles.emptyState}>Loading invoices…</div>
      ) : sorted.length === 0 ? (
        <div style={styles.emptyState}>
          {filter === "all"
            ? "No outstanding invoices. You're all caught up!"
            : `No ${filter.replace("_", " ")} invoices.`}
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={{ ...styles.th, width: "30%" }}>Client</th>
                <th style={{ ...styles.th, width: "26%" }}>Description</th>
                <th style={{ ...styles.th, width: "14%", textAlign: "right" }}>Amount</th>
                <th style={{ ...styles.th, width: "14%" }}>Due Date</th>
                <th style={{ ...styles.th, width: "16%", textAlign: "center" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((inv, idx) => {
                const urgency = STATUS_COLORS[inv.urgency];
                const clientName =
                  inv.clients?.business_name || inv.clients?.full_name || "—";
                const subName =
                  inv.clients?.business_name ? inv.clients.full_name : null;

                return (
                  <tr
                    key={inv.id}
                    style={{
                      ...styles.row,
                      backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                    }}
                  >
                    {/* Client */}
                    <td style={styles.td}>
                      <div style={styles.clientName}>{clientName}</div>
                      {subName && (
                        <div style={styles.clientSub}>{subName}</div>
                      )}
                      <div style={styles.invoiceNum}>{inv.invoice_number}</div>
                    </td>

                    {/* Description */}
                    <td style={styles.td}>
                      <div style={styles.description}>{inv.description || "—"}</div>
                    </td>

                    {/* Amount */}
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <span style={styles.amount}>
                        {formatCurrency(inv.total_amount)}
                      </span>
                    </td>

                    {/* Due Date */}
                    <td style={styles.td}>
                      <div style={styles.dueDate}>{formatDate(inv.due_date)}</div>
                      <div
                        style={{
                          ...styles.daysLabel,
                          color: urgency.text,
                        }}
                      >
                        {getDaysLabel(inv.due_date)}
                      </div>
                    </td>

                    {/* Status Badge */}
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <span
                        style={{
                          ...styles.badge,
                          backgroundColor: urgency.bg,
                          color: urgency.text,
                        }}
                      >
                        <span
                          style={{
                            ...styles.badgeDot,
                            backgroundColor: urgency.dot,
                          }}
                        />
                        {urgency.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer total */}
      {sorted.length > 0 && (
        <div style={styles.footer}>
          <span style={styles.footerLabel}>
            {sorted.length} invoice{sorted.length !== 1 ? "s" : ""}
            {filter !== "all" ? ` (${filter.replace("_", " ")})` : ""}
          </span>
          <span style={styles.footerTotal}>{formatCurrency(totalOutstanding)}</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    background: "#fff",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "20px 24px 16px",
    borderBottom: "1px solid #F3F4F6",
  },
  title: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    margin: "3px 0 0",
    fontSize: "13px",
    color: "#6B7280",
  },
  totalHighlight: {
    color: "#111827",
    fontWeight: 600,
  },
  refreshBtn: {
    background: "none",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "16px",
    cursor: "pointer",
    color: "#6B7280",
    lineHeight: 1,
  },
  alertBanner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 24px",
    backgroundColor: "#FEF2F2",
    borderBottom: "1px solid #FEE2E2",
    fontSize: "13px",
    color: "#991B1B",
  },
  alertDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#EF4444",
    flexShrink: 0,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 24px",
    borderBottom: "1px solid #F3F4F6",
    gap: "12px",
    flexWrap: "wrap",
  },
  filters: {
    display: "flex",
    gap: "6px",
  },
  filterBtn: {
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 500,
    border: "1px solid #E5E7EB",
    borderRadius: "20px",
    background: "#fff",
    color: "#6B7280",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    transition: "all 0.15s",
  },
  filterBtnActive: {
    background: "#111827",
    borderColor: "#111827",
    color: "#fff",
  },
  filterCount: {
    fontSize: "11px",
    fontWeight: 600,
    opacity: 0.75,
  },
  sortSelect: {
    fontSize: "12px",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    padding: "5px 10px",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
    outline: "none",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  thead: {
    backgroundColor: "#F9FAFB",
  },
  th: {
    padding: "10px 16px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid #F3F4F6",
    whiteSpace: "nowrap",
  },
  row: {
    borderBottom: "1px solid #F3F4F6",
    transition: "background 0.1s",
  },
  td: {
    padding: "12px 16px",
    verticalAlign: "middle",
  },
  clientName: {
    fontWeight: 600,
    color: "#111827",
    fontSize: "13px",
  },
  clientSub: {
    fontSize: "11px",
    color: "#6B7280",
    marginTop: "1px",
  },
  invoiceNum: {
    fontSize: "11px",
    color: "#9CA3AF",
    marginTop: "2px",
    fontFamily: "monospace",
  },
  description: {
    color: "#374151",
    lineHeight: 1.4,
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  amount: {
    fontWeight: 700,
    fontSize: "14px",
    color: "#111827",
    fontVariantNumeric: "tabular-nums",
  },
  dueDate: {
    color: "#374151",
    fontWeight: 500,
  },
  daysLabel: {
    fontSize: "11px",
    fontWeight: 500,
    marginTop: "2px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  badgeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 24px",
    borderTop: "1px solid #F3F4F6",
    backgroundColor: "#FAFAFA",
  },
  footerLabel: {
    fontSize: "12px",
    color: "#6B7280",
    fontWeight: 500,
  },
  footerTotal: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#111827",
    fontVariantNumeric: "tabular-nums",
  },
  emptyState: {
    padding: "40px 24px",
    textAlign: "center",
    color: "#9CA3AF",
    fontSize: "14px",
  },
};

